import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { ChevronLeft, ChevronRight, Quote, Star, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [allTestimonials, setAllTestimonials] = useState([]);
  const [stats, setStats] = useState({ overall_rating: 0, total_ratings: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      // Only fetch Google reviews (no local testimonials)
      try {
        const googleRes = await axios.get(`${BACKEND_URL}/api/public/google-reviews`);
        const data = googleRes.data || {};
        const reviews = data.reviews || [];
        const reviewStats = data.stats || { overall_rating: 0, total_ratings: 0 };

        setStats(reviewStats);

        const googleData = reviews.map(review => ({
          id: `google-${review.time}`,
          name: review.author_name,
          role: 'Google Reviewer',
          company: 'Google',
          content: review.text,
          rating: review.rating,
          image: null, // Will use initials instead
          source: 'google',
          author_url: review.author_url
        }));

        if (googleData.length > 0) {
          // Already sorted by most recent in backend
          setAllTestimonials(googleData);
        }
      } catch (err) {
        console.error('Failed to fetch Google reviews:', err);
      }
    } catch (error) {
      console.error('General error in fetchTestimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAutoPlaying && allTestimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % allTestimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, allTestimonials.length]);

  const nextTestimonial = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % allTestimonials.length);
  };

  const prevTestimonial = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + allTestimonials.length) % allTestimonials.length);
  };

  useEffect(() => {
    if (currentIndex >= allTestimonials.length && allTestimonials.length > 0) {
      setCurrentIndex(0);
    }
  }, [allTestimonials.length, currentIndex]);

  // Show loading or return null if no data after loading
  if (allTestimonials.length === 0) {
    if (loading) {
      return (
        <section className="py-20 bg-blue-900">
          <div className="container mx-auto px-4 text-center">
            <div className="text-white">Loading testimonials...</div>
          </div>
        </section>
      );
    }
    // Don't hide section completely, just don't show it if no data
    return null;
  }

  const current = allTestimonials[currentIndex];

  if (!current) {
    console.log('Testimonials: Current item is undefined', { currentIndex, length: allTestimonials.length });
    return null;
  }

  console.log('Testimonials: Rendering', { name: current.name, source: current.source });

  return (
    <section id="testimonials-section" className="py-20 bg-blue-900 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20"></div>
      <div className="absolute bottom-10 right-10 w-64 h-64 bg-blue-800 rounded-full blur-3xl opacity-20"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            What Our Professionals Say
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Hear from professionals who have transformed their careers with ISDE Academy
          </p>

          {/* Overall Rating Stats */}
          {stats.total_ratings > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center gap-3 pt-4"
            >
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-lg rounded-full px-6 py-3 border border-white/20">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-5 w-5 ${i < Math.floor(stats.overall_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-white/30'}`}
                    />
                  ))}
                </div>
                <span className="text-2xl font-bold text-white">{stats.overall_rating}</span>
              </div>
              <div className="text-white/90 text-lg">
                <span className="font-bold">{stats.total_ratings}</span> reviews on
                <img
                  src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_light_color_92x30dp.png"
                  alt="Google"
                  className="inline h-4 ml-1.5 brightness-0 invert"
                />
              </div>
            </motion.div>
          )}
        </div>

        {/* Testimonial Carousel */}
        <div className="max-w-4xl mx-auto relative">
          <Card className="bg-white/95 backdrop-blur-sm border-none shadow-2xl">
            <CardContent className="p-8 lg:p-12">
              <div className="space-y-6">
                {/* Header with Rating and Source Badge */}
                <div className="flex items-start justify-between gap-4 pb-4 border-b border-gray-200">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-6 w-6 ${i < current.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  {current.source === 'google' && (
                    <div className="flex items-center gap-2">
                      <img
                        src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
                        alt="Google"
                        className="h-5 object-contain"
                      />
                      <a
                        href={current.author_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 flex items-center gap-1 hover:underline whitespace-nowrap"
                      >
                        View on Google <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>

                {/* Review Content */}
                <p className="text-xl text-gray-700 leading-relaxed">
                  "{current.content}"
                </p>

                {/* Author Info */}
                <div className="flex items-center gap-4 pt-4">
                  {/* Avatar with Initials or Image */}
                  {current.image ? (
                    <img
                      src={current.image}
                      alt={current.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-blue-200 shadow-md flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center border-2 border-blue-200 shadow-md flex-shrink-0">
                      <span className="text-white text-xl font-bold">
                        {getInitials(current.name)}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-lg text-gray-900 truncate">
                      {current.name}
                    </div>
                    <div className="text-blue-600 font-medium truncate">
                      {current.role}
                    </div>
                    {current.company && current.source !== 'google' && (
                      <div className="text-sm text-gray-600 mt-1 truncate">
                        {current.company}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <button
            onClick={prevTestimonial}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-all hover:scale-110 hidden lg:flex"
          >
            <ChevronLeft className="h-6 w-6 text-blue-600" />
          </button>
          <button
            onClick={nextTestimonial}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-all hover:scale-110 hidden lg:flex"
          >
            <ChevronRight className="h-6 w-6 text-blue-600" />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {allTestimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setIsAutoPlaying(false);
                }}
                className={`w-3 h-3 rounded-full transition-all ${index === currentIndex
                  ? 'bg-white w-8'
                  : 'bg-blue-300 hover:bg-blue-200'
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
